// What Members shows of a profile (plan 5B), used by the server and the browser.

export const LINK_STATES = ['', 'verified', 'pre_migration'] as const;
export type LinkState = (typeof LINK_STATES)[number];

export const ORG_STATUSES = ['none', 'member', 'affiliate', 'pending'] as const;
export type OrgStatus = (typeof ORG_STATUSES)[number];

export const AGREEMENTS = ['', 'agreed', 'rejected'] as const;
export type Agreement = (typeof AGREEMENTS)[number];

/** The roster's filters. `unlinked` is "in the org, and not linked". */
export const SHOWS = ['all', 'linked', 'rsi_only', 'threats', 'unlinked'] as const;
export type Show = (typeof SHOWS)[number];

export const SHOW_LABELS: Readonly<Record<Show, string>> = {
  all: 'All',
  linked: 'Linked',
  rsi_only: 'RSI only',
  threats: 'Threats',
  unlinked: 'Not linked, in the org',
};

/** What a link is, in words. A link carried over from ITCBot was never proven here. */
export function linkLabel(state: LinkState): string {
  if (state === 'verified') return 'Linked';
  if (state === 'pre_migration') return 'Linked before migration';
  return 'Not linked';
}

export const ORG_STATUS_LABELS: Readonly<Record<OrgStatus, string>> = {
  none: 'Not in the org',
  member: 'Member',
  affiliate: 'Affiliate',
  pending: 'Applied',
};

/** Where the RSI cookies stand. `unknown` is set but not yet shown to work; `invalid` stops logged-in work until a test passes. */
export const TOKEN_STATUSES = ['unset', 'unknown', 'valid', 'invalid'] as const;
export type TokenStatus = (typeof TOKEN_STATUSES)[number];

export const TOKEN_STATUS_LABELS: Readonly<Record<TokenStatus, string>> = {
  unset: 'Not set',
  unknown: 'Set, not yet tested',
  valid: 'Working',
  invalid: 'Not working',
};

/** What a cookie's value may hold (RFC 6265's cookie-octets): nothing that could end the header or add another cookie. */
export const COOKIE_VALUE = /^[\x21\x23-\x2b\x2d-\x3a\x3c-\x5b\x5d-\x7e]{8,512}$/;

/** Where someone is in /join, as the session holds it. A finished run has no session. */
export const JOIN_STEPS = ['verify', 'unredact', 'agreement'] as const;
export type JoinStep = (typeof JOIN_STEPS)[number];

/** How a /join run ended. `threat` is any result that still holds a flag, including one the person has agreed to. */
export const JOIN_OUTCOMES = ['clean', 'threat', 'evaded', 'rejected', 'hostile'] as const;
export type JoinOutcome = (typeof JOIN_OUTCOMES)[number];

/** The two kinds of sync pass. */
export const SYNC_KINDS = ['roster', 'public'] as const;
export type SyncKind = (typeof SYNC_KINDS)[number];

/** `idle` is never run; `running` is a pass under way, even between runs. */
export const SYNC_STATUSES = ['idle', 'running', 'ok', 'failed'] as const;
export type PassState = (typeof SYNC_STATUSES)[number];

export const PASS_STATE_LABELS: Readonly<Record<PassState, string>> = {
  idle: 'Never run',
  running: 'Running',
  ok: 'Finished',
  failed: 'Failed',
};

/** What a pass has done, in a line. `counts` are the numbers the server keeps by name. */
export function describeCounts(kind: SyncKind, state: PassState, counts: Readonly<Record<string, number>>): string {
  const n = (name: string) => counts[name] ?? 0;
  if (kind === 'roster') {
    if (state === 'idle' || state === 'running') return '';
    if (state === 'failed') return n('rows') > 0 ? `Saw ${String(n('rows'))} people. Nothing was changed.` : 'Nothing was changed.';
    return `${String(n('rows'))} people: ${String(n('created'))} new, ${String(n('updated'))} changed, ${String(n('left'))} left`;
  }
  if (state === 'idle') return '';
  const done = n('scraped') + n('failed') + n('conflicts');
  const parts = [`${String(done)} of ${String(n('total'))}`, `${String(n('changed'))} changed`];
  if (n('failed') > 0) parts.push(`${String(n('failed'))} could not be read`);
  if (n('moved') > 0) parts.push(`${String(n('moved'))} moved to a new handle`);
  if (n('conflicts') > 0) parts.push(`${String(n('conflicts'))} handle changes need a look`);
  return parts.join(', ');
}
